/**
 * 派单页面 Redux Slice
 * 管理选中订单、批量派单、单项目预约等状态
 */
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { singleProjectAppointment, cancelSingleProjectAppointment } from '@/service/dispatchPage'

// ==================== 初始状态 ====================

const initialState = {
  // 选中订单ID（用于右侧展示详情）
  selectedOrderId: null,
  // 单项目预约
  singleAppointment: {
    fetching: false,
    error: null
  },
  // 取消单项目预约
  cancelSingleAppointment: {
    fetching: false,
    error: null
  }
}

// ==================== 异步 Action ====================

/**
 * 产康单项目预约
 * 参数由操作层（如 SingleProjectAppointmentModal）组好，此处仅透传
 * @param {Object} payload - 操作层组好的请求体，直接传给 service
 */
export const singleProjectAppointmentThunk = createAsyncThunk(
  'dispatchPage/singleProjectAppointment',
  async (payload, { rejectWithValue }) => {
    try {
      const response = await singleProjectAppointment(payload)
      return { data: response.data }
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message || '预约失败')
    }
  }
)

/**
 * 取消产康单项目预约
 * @param {Object} payload - { id } 为安排 ID（ProjectPlanVO.id）；历史兼容 { memberProjectId }仅在不传 id 时使用
 */
export const cancelSingleProjectAppointmentThunk = createAsyncThunk(
  'dispatchPage/cancelSingleProjectAppointment',
  async (payload, { rejectWithValue }) => {
    try {
      const id = payload.id ?? payload.memberProjectId
      const response = await cancelSingleProjectAppointment(id)
      return { data: response.data }
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message || '取消预约失败')
    }
  }
)

// ==================== Slice ====================

const dispatchPageSlice = createSlice({
  name: 'dispatchPage',
  initialState,
  reducers: {
    /** 设置选中订单 */
    setSelectedOrderId: (state, action) => {
      state.selectedOrderId = action.payload
    },
    /** 清空选中订单（切换门店/入住状态时调用） */
    clearSelectedOrder: (state) => {
      state.selectedOrderId = null
    }
  },
  extraReducers: (builder) => {
    builder
      // ========== 单项目预约 ==========
      .addCase(singleProjectAppointmentThunk.pending, (state) => {
        state.singleAppointment.fetching = true
        state.singleAppointment.error = null
      })
      .addCase(singleProjectAppointmentThunk.fulfilled, (state) => {
        state.singleAppointment.fetching = false
        state.singleAppointment.error = null
      })
      .addCase(singleProjectAppointmentThunk.rejected, (state, action) => {
        state.singleAppointment.fetching = false
        state.singleAppointment.error = action.payload
      })
      // ========== 取消单项目预约 ==========
      .addCase(cancelSingleProjectAppointmentThunk.pending, (state) => {
        state.cancelSingleAppointment.fetching = true
        state.cancelSingleAppointment.error = null
      })
      .addCase(cancelSingleProjectAppointmentThunk.fulfilled, (state) => {
        state.cancelSingleAppointment.fetching = false
        state.cancelSingleAppointment.error = null
      })
      .addCase(cancelSingleProjectAppointmentThunk.rejected, (state, action) => {
        state.cancelSingleAppointment.fetching = false
        state.cancelSingleAppointment.error = action.payload
      })
  }
})

export const {
  setSelectedOrderId,
  clearSelectedOrder
} = dispatchPageSlice.actions

export default dispatchPageSlice.reducer
